import { useAuthStore } from '../../../context/AuthContext';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useStowageUIStore } from 'src/context/StowageUIContext';
import { createEmpyStowagePlan, TITLE_MAX_LENGTH } from 'src/factory/Stowage/StowagePlanFactory';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { sortStowagePlans } from 'src/utils/stowage/stowagePlanUtils';
import { useEffect, useState } from 'react';
import OdfjellInputComponent from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';

const PageHeaderStowageCopy = () => {
    const { activeStowagePlan, setActiveStowagePlan, setStowagePlans } = useStowagePlanStore();
    const { setIsCopyStowagePlanMenuOpen } = useStowageUIStore();
    const { loggedInUser } = useAuthStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();
    const { addStowagePlan } = usePostStowageApi();
    const [newTitle, setNewTitle] = useState<string>('');
    useEffect(() => {
        const _baseTitle = activeStowagePlan.title;
        let _newTitle = '';

        if (_baseTitle.startsWith('Copy of ')) {
            const _copyNumberMatch = _baseTitle.match(/Copy of .+?(\(\d+\))?$/);
            const _currentNumber = _copyNumberMatch?.[1] ? parseInt(_copyNumberMatch[1].replace(/[()]/g, '')) : 0;
            const _baseTitleWithoutNumber = _baseTitle.replace(/\(\d+\)$/, '').trim();
            _newTitle = `${_baseTitleWithoutNumber} (${_currentNumber + 1})`;
        } else {
            _newTitle = 'Copy of ' + _baseTitle;
        }

        if (_newTitle.length > TITLE_MAX_LENGTH) {
            _newTitle = _newTitle.slice(0, TITLE_MAX_LENGTH);
        }
        setNewTitle(_newTitle);
    }, [activeStowagePlan.title]);

    const onCopyStowagePlan = async () => {
        const _stowagePlan = createEmpyStowagePlan(
            activeStowagePlan.imoNumber,
            loggedInUser.userName,
            activeStowagePlan.stowagePlanGuid,
            newTitle,
            activeStowagePlan.masterPlanVersion
        );
        const { updatedStowagePlan, updatedStowagePlans } = await addStowagePlan(_stowagePlan, true);

        if (updatedStowagePlan && updatedStowagePlans) {
            // Populate the copy from the current active plan's data + new plan metadata.
            // This avoids a backend round-trip — all data is already in memory.
            const populatedCopy = {
                ...activeStowagePlan,
                stowagePlanGuid: updatedStowagePlan.stowagePlanGuid,
                parentGuid: updatedStowagePlan.parentGuid,
                title: updatedStowagePlan.title,
                comments: updatedStowagePlan.comments,
                type: updatedStowagePlan.type,
                shareType: updatedStowagePlan.shareType,
                owner: updatedStowagePlan.owner,
                initials: updatedStowagePlan.initials,
                lastUpdatedBy: updatedStowagePlan.lastUpdatedBy,
                lastUpdatedUtc: updatedStowagePlan.lastUpdatedUtc,
                createdDateTimeUtc: updatedStowagePlan.createdDateTimeUtc,
                syncedWithImosDateTimeUtc: updatedStowagePlan.syncedWithImosDateTimeUtc,
                syncedWithParentDateTimeUtc: updatedStowagePlan.syncedWithParentDateTimeUtc,
                isDeleted: updatedStowagePlan.isDeleted,
                masterPlanVersion: updatedStowagePlan.masterPlanVersion,
                // Keep isNewStowagePlan: true so handleStowagePlanGuidChange uses the one-shot
                // signal path (consume-and-return) instead of fetching from backend. The early
                // exit (activeStowagePlan.stowagePlanGuid match + rotation.length > 0) is a
                // secondary guard but fails for plans without rotation data.
                isNewStowagePlan: true,
            };
            // Replace the empty shell that addStowagePlan added with the fully populated copy.
            // Without this, stowagePlans holds only the empty shell (no rotation/cargo data) and
            // the plan either does not appear in the selection list or shows blank content on
            // re-navigation after the user returns to the master plan.
            const plansWithPopulatedCopy = sortStowagePlans(
                updatedStowagePlans.map((sp) =>
                    sp.stowagePlanGuid === updatedStowagePlan.stowagePlanGuid ? populatedCopy : sp
                )
            );
            setStowagePlans(plansWithPopulatedCopy);
            // Active plan should not be flagged as "new" in header/UI actions.
            // Keep the one-shot flag only in stowagePlans for URL re-navigation handling.
            setActiveStowagePlan({ ...populatedCopy, isNewStowagePlan: null });
            setQueryParamStowagePlan(updatedStowagePlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        }
        setIsCopyStowagePlanMenuOpen(false);
    };

    const onClose = () => {
        setIsCopyStowagePlanMenuOpen(false);
    };

    return (
        <OdfjellPanel
            headerTitle="Copy stowage plan"
            defaultPosition="center"
            panelWidth={PANEL_CONSTANTS.MEDIUM_WIDTH}
            hasCurtain={true}
            preventOutsideClose={true}
            onCancel={onClose}
            onAction={onCopyStowagePlan}
            showCancelButton={true}
        >
            <li className="dd-item">
                <OdfjellInputComponent
                    description="Title"
                    type="text"
                    isReadOnly={false}
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.event.target.value)}
                    maxTextLength={50}
                />
            </li>
        </OdfjellPanel>
    );
};

export default PageHeaderStowageCopy;
